/**
 * InAppWebView (web)
 *
 * Web variant — there is no WebView on web (and we're not using iframes).
 * Instead, when `visible` becomes true we dispatch by URL kind:
 *   - **Internal app URL** (same-origin or short-link domain) → replace the
 *     current location so the linking config picks it up. The native flow
 *     opens these in a modal, but on web they should just be a route — once
 *     we land on `/emergency/:token` or the resolved short-link target, the
 *     navigator handles rendering.
 *   - **External URL** (OAuth, Fasten widget, etc.) → `window.open` in a new
 *     tab. The user finishes the flow there and the embedding app picks up
 *     the result via the redirect URL or a `message` listener.
 *
 * Either way we call `onClose` immediately so the (invisible) modal unmounts.
 */

import React, { useEffect } from 'react';

interface Props {
  visible: boolean;
  uri: string;
  title?: string;
  onClose: () => void;
  onMessage?: (data: any) => void;
  injectedJavaScript?: string;
}

const APP_HOSTS = [
  'app.eleplan.com',
  'development.eleplan.org',
  'eleplan.link',
  'development.eleplan.link',
  'localhost',
];

function isInternalUrl(uri: string): boolean {
  try {
    const u = new URL(uri, typeof window !== 'undefined' ? window.location.href : 'https://app.eleplan.com');
    if (!u.hostname) return true;
    if (typeof window !== 'undefined' && u.hostname === window.location.hostname) return true;
    return APP_HOSTS.some((h) => u.hostname === h || u.hostname.endsWith(`.${h}`));
  } catch {
    return false;
  }
}

export default function InAppWebView({ visible, uri, onClose }: Props) {
  useEffect(() => {
    if (!visible || !uri) return;
    if (typeof window === 'undefined') return;
    // Defer one tick so React commit completes before we navigate / open. This
    // also lets the parent finish whatever side effect it queued before
    // showing the WebView (e.g. setting a `pendingRegisterPrefill`).
    const timer = setTimeout(() => {
      if (isInternalUrl(uri)) {
        // For app-internal URLs, navigate the current tab. The linking config
        // resolves /emergency/:token, /short-link/:slug, etc. into the
        // appropriate native screen. We use href (not pushState) so a refresh
        // works and so the URL is in history.
        window.location.href = uri;
      } else {
        // External URLs (Google OAuth, Fasten widget, etc.) open in a new tab.
        // `noopener` so the popup can't reach back into our window.
        const popup = window.open(uri, '_blank', 'noopener');
        // Some browsers block window.open when not directly tied to a user
        // gesture. Fall back to same-tab navigation in that case so the user
        // isn't stuck — they can use the back button to return.
        if (!popup) window.location.href = uri;
      }
      onClose();
    }, 0);
    return () => clearTimeout(timer);
  }, [visible, uri, onClose]);

  // No DOM output — the redirect/popup is the entire UX on web.
  return null;
}
